import React from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Image, Button, Popconfirm, Row, Col } from 'antd';
import { DeleteOutlined, HolderOutlined } from '@ant-design/icons';
import { snsPostFilesApi } from '../api/endpoints';
import type { SnsPostFile } from '../api/types';

interface SortablePhotoProps {
  file: SnsPostFile;
  onDelete: (id: number) => void;
}

const SortablePhoto: React.FC<SortablePhotoProps> = ({ file, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: file.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative' as const,
  };

  return (
    <Col span={8} ref={setNodeRef} style={style}>
      <div style={{ position: 'relative', transition: 'transform 0.2s ease' }}>
        <div
          {...attributes}
          {...listeners}
          role="button"
          aria-label="写真の順番を変更"
          style={{
            position: 'absolute',
            top: 4,
            left: 4,
            zIndex: 10,
            cursor: 'grab',
            background: 'rgba(0,0,0,0.5)',
            borderRadius: 4,
            padding: '2px 4px',
            color: '#fff',
            fontSize: 14,
            lineHeight: 1,
            transition: 'opacity 0.2s ease',
          }}
        >
          <HolderOutlined />
        </div>
        <Image
          src={snsPostFilesApi.thumbnail(file.id)}
          alt={file.original_filename}
          style={{ borderRadius: 8, objectFit: 'cover', width: '100%', aspectRatio: '1' }}
          preview={{ src: `/api/v1/sns-post-files/${file.id}/download` }}
        />
        <Popconfirm
          title="この写真を削除しますか？"
          onConfirm={() => onDelete(file.id)}
          okText="削除"
          cancelText="キャンセル"
        >
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            aria-label="この写真を削除"
            style={{ position: 'absolute', top: 4, right: 4, opacity: 0.8, minWidth: 32, minHeight: 32, transition: 'opacity 0.2s ease' }}
          />
        </Popconfirm>
      </div>
    </Col>
  );
};

interface SortablePhotoGridProps {
  files: SnsPostFile[];
  onDelete: (id: number) => void;
  onReorder: (fileIds: number[]) => void;
}

const SortablePhotoGrid: React.FC<SortablePhotoGridProps> = ({ files, onDelete, onReorder }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = files.findIndex(f => f.id === active.id);
    const newIndex = files.findIndex(f => f.id === over.id);

    const reordered = [...files];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);

    onReorder(reordered.map(f => f.id));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={files.map(f => f.id)} strategy={rectSortingStrategy}>
        <Image.PreviewGroup>
          <Row gutter={[8, 8]}>
            {files.map(file => (
              <SortablePhoto key={file.id} file={file} onDelete={onDelete} />
            ))}
          </Row>
        </Image.PreviewGroup>
      </SortableContext>
    </DndContext>
  );
};

export default SortablePhotoGrid;
